import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useI18n } from '../contexts/I18nContext';
import LanguageSelector from './LanguageSelector';

const HeaderContainer = styled.header`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--primary-200);
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: var(--shadow-sm);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-4) var(--space-8);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-4);
  }

  @media (max-width: 480px) {
    padding: var(--space-3) var(--space-2);
  }
`;

const Logo = styled(Link)`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--warm-700);
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  letter-spacing: -0.025em;

  &:hover {
    color: var(--warm-800);
    transform: translateY(-1px);
  }

  &:focus {
    outline: 2px solid var(--accent-400);
    outline-offset: 2px;
    border-radius: var(--radius-sm);
  }

  @media (max-width: 768px) {
    font-size: var(--font-size-lg);
  }

  @media (max-width: 480px) {
    font-size: var(--font-size-base);
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: var(--space-8);
  align-items: center;

  @media (max-width: 768px) {
    gap: var(--space-4);
  }

  @media (max-width: 480px) {
    gap: var(--space-3);
    justify-content: flex-end;
  }
`;

const NavLink = styled(Link)<{ isActive: boolean }>`
  font-family: 'Inter', sans-serif;
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: ${props => props.isActive ? 'var(--warm-700)' : 'var(--primary-600)'};
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  background: transparent;

  &:hover {
    color: var(--warm-700);
    background: var(--primary-50);
    transform: translateY(-1px);
  }

  &:focus {
    outline: 2px solid var(--accent-400);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: var(--space-2) var(--space-3);
    font-size: var(--font-size-xs);
  }

  @media (max-width: 480px) {
    padding: var(--space-2) var(--space-2);
    font-size: var(--font-size-xs);
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  ${props => props.isActive && `
    color: var(--warm-700);
    background: var(--warm-50);

    &:after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 24px;
      height: 2px;
      background: var(--warm-500);
      border-radius: var(--radius-full);
    }

    @media (max-width: 480px) {
      background: var(--warm-100);
      color: var(--warm-800);
    }
  `}
`;

const Header: React.FC = () => {
  const { t } = useI18n();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">{t('site.title')}</Logo>
        <Nav>
          <NavLink to="/" isActive={isActive('/')}>
            {t('navigation.home')}
          </NavLink>
          <NavLink to="/chapters" isActive={isActive('/chapters')}>
            {t('navigation.chapters')}
          </NavLink>
          <NavLink to="/about" isActive={isActive('/about')}>
            {t('navigation.about')}
          </NavLink>
          <LanguageSelector />
        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;